<template>
	<div class="filter-chip-button-container d-flex">
		<div
			class="scrollable-wrappers"
			:style="{ width: sortChip && !isSmallScreen ? '420px' : '600px' }"
		>
			<div class="button_filter_container" v-if="needScroll && canScrollLeft">
				<v-btn icon class="scroll-button" @click="moveLeft" color="#002855">
					<Icon type="chevron_left" :size="14" />
				</v-btn>
			</div>
			<div class="scrollable-container" ref="scrollContainer">
				<div
					class="scrollable-content"
					:style="{
						transform: `translateX(${translateX}px)`,
						paddingRight: `${rightPadding}px`,
					}"
				>
					<v-btn
						v-for="chipItem in otherChips"
						:key="chipItem.name"
						@click="handleChipClick(getChipName(chipItem), getChipLabel(chipItem))"
						class="filter-chip-button"
						:class="{
							'filter-chip-button-active': isButtonActive(getChipName(chipItem)),
						}"
					>
						<v-icon
							@click.stop="clearFilter(getChipName(chipItem))"
							v-if="
								isButtonActive(getChipName(chipItem)) &&
								getChipName(chipItem) !== 'clear'
							"
						>
							mdi-close
						</v-icon>
						{{ getChipLabel(chipItem) }}
					</v-btn>
					<div v-if="sortChip && isSmallScreen" class="sort-button-container">
						<v-btn
							ref="sort"
							class="sort-button"
							:class="{ 'sort-button-active': isButtonActive(sortChip.name) }"
							@click="handleChipClick(sortChip.name, sortChip.content)"
						>
							<Icon class="mr-2" type="sort" color="#002855" :size="20" />
							Sort By
						</v-btn>
					</div>
				</div>
			</div>
			<div class="button_filter_container_left" v-if="needScroll && !scrollRightStop">
				<v-btn icon class="scroll-button" @click="moveRight">
					<Icon type="chevron_right" :size="14" color="#002855" />
				</v-btn>
			</div>
		</div>
		<div v-if="sortChip && !isSmallScreen" class="sort-button-container">
			<v-btn
				ref="sort"
				class="sort-button"
				:class="{ 'sort-button-active': isButtonActive(sortChip.name) }"
				@click="handleChipClick(sortChip.name, sortChip.content)"
			>
				<Icon class="mr-2" type="sort" color="#002855" :size="20" />
				Sort By
			</v-btn>
		</div>
	</div>
</template>

<script>
import { mapActions } from 'vuex'

export default {
	name: 'FilterChipButton',
	props: {
		useContentAsKey: {
			type: Boolean,
			default: false,
		},
		isButtonActive: {
			type: Function,
			required: true,
		},
		chipsList: {
			type: Array,
			default: () => [],
		},
		clearFilter: {
			type: Function,
			required: true,
		},
	},
	mounted() {
		window.addEventListener('resize', this.handleResize)
		this.updateScreenSize()
		this.$nextTick(this.updateScrollState)
	},
	beforeDestroy() {
		window.removeEventListener('resize', this.handleResize)
	},
	data() {
		return {
			scrollRightStop: false,
			maxTranslateX: 0,
			translateX: 0,
			rightPadding: 0,
			isSmallScreen: false,
		}
	},
	methods: {
		updateScreenSize() {
			this.isSmallScreen = window.innerWidth <= 540
		},
		getChipName(chipItem) {
			return this.useContentAsKey ? chipItem.content : chipItem.name
		},
		getChipLabel(chipItem) {
			return this.useContentAsKey ? chipItem.name : chipItem.content
		},
		resetScroll() {
			this.translateX = 0
			this.scrollRightStop = false
			this.rightPadding = 0
		},
		moveLeft() {
			this.translateX = Math.min(0, this.translateX + 100)
			this.rightPadding = 0
			this.updateScrollState()
		},
		moveRight() {
			const scrollStep = 100
			this.translateX = Math.max(-this.maxScrollWidth - 10, this.translateX - scrollStep)

			if (this.translateX <= -this.maxScrollWidth) {
				this.rightPadding = 10
			} else {
				this.rightPadding = 0
			}

			this.updateScrollState()
		},
		updateScrollState() {
			this.scrollRightStop = this.translateX <= -this.maxScrollWidth - 10
		},
		handleResize() {
			this.updateScreenSize()
			this.translateX = Math.max(-this.maxScrollWidth, this.translateX)
			this.updateScrollState()
		},
		handleChipClick(name, label) {
			this.$emit('handleChipClick', { name, label })
		},
	},
	computed: {
		sortChip() {
			return this.chipsList.find((chip) => chip.name === 'sort')
		},
		otherChips() {
			return this.chipsList.filter((chip) => chip.name !== 'sort')
		},
		maxScrollWidth() {
			if (!this.$refs.scrollContainer) return 0
			const containerWidth = this.$refs.scrollContainer.offsetWidth
			const contentWidth = this.$refs.scrollContainer.scrollWidth
			return Math.max(0, contentWidth - containerWidth + this.rightPadding + 20)
		},
		needScroll() {
			return this.chipsList.length > 5
		},
		canScrollLeft() {
			return this.translateX < 0
		},
	},
	watch: {
		$route() {
			this.resetScroll()
		},
	},
}
</script>

<style lang="scss">
.filter-chip-button-container {
	display: flex;
	justify-content: space-between;

	@media (max-width: 480px) {
		justify-content: flex-start;
	}
}

.scrollable-wrappers {
	display: flex;
	justify-content: space-between;
	align-items: center;
	width: 420px;
	@media (max-width: 480px) {
		width: 100%;
		flex-direction: column;
		align-items: flex-start;
		height: unset !important;
	}

	@media (max-width: 480px) {
		justify-content: center;
	}
}

.sort-button {
	display: flex;
	align-items: center;
	justify-content: center;
	width: 120px;
	height: 32px;
	padding: 8px 16px;
	font-family: 'Montserrat', sans-serif !important;
	font-weight: bold;
	font-size: 16px;
	letter-spacing: 0.36px;
	line-height: 19.5px;
	color: var(--chip-text-normal) !important;
	background-color: var(--chip-normal-background) !important;
	border-radius: 8px;
	text-transform: none;
	box-shadow: 0 1px 1px 0 rgba(0, 0, 0, 0.1);

	&-active {
		background-color: var(--chip-normal-background) !important;
		border: 1px solid #0055a3;
		color: var(--chip-text-normal) !important;
		box-shadow: 0 1px 1px 0 rgba(0, 0, 0, 0.1);
	}
}

.scrollable-container {
	overflow: hidden;
	flex-grow: 0;

	@media (max-width: 480px) {
		max-width: 350px;
		overflow: visible;
		width: 100% !important;
		display: flex;
		justify-content: center;
		align-items: center;
	}
}

.scrollable-content {
	display: flex;
	gap: 12px;
	transition: transform 0.3s ease, padding-right 0.3s ease;

	@media (max-width: 480px) {
		flex-wrap: wrap;
		transform: none !important;
	}
}

.filter-chip-button {
	margin-top: 1px;
	color: var(--chip-text-normal) !important;
	font-size: 14px;
	font-family: Montserrat, sans-serif;
	font-weight: bold;
	letter-spacing: 0.36px;
	background-color: var(--chip-normal-background) !important;
	border-radius: 8px;
	box-shadow: 0 1px 1px 0 rgba(0, 0, 0, 0.1);
	text-transform: capitalize !important;
	align-items: center;

	&-active {
		background-color: var(--chip-selected-background) !important;
		color: var(--chip-text-selected) !important;
	}
}
</style>
